import React from "react";
import ReactECharts from "echarts-for-react";
import { Spin, Empty } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

interface ProjectCompletionDonutChartProps {
  loading: boolean;
  /**
   * API gives us paired percentages (`closedePCR` + `pendingTasksPCR` that sum
   * to 100) AND task counts (`closedTaskCount`, `pendingTaskCount`). The pie
   * is driven by the percentages so slices and the centre label always agree;
   * counts are surfaced in the tooltip for context.
   */
  chartData: {
    completedPercent: number;
    pendingPercent: number;
    completedCount?: number;
    pendingCount?: number;
  } | null;
}

const ProjectCompletionDonutChart: React.FC<
  ProjectCompletionDonutChartProps
> = ({ loading, chartData }) => {
  const getChartOptions = () => {
    if (!chartData) return {};

    const completedPct = Math.max(
      0,
      Math.min(100, Number(chartData.completedPercent) || 0)
    );
    // Trust the API value if present; otherwise derive from completed.
    const rawPending = Number(chartData.pendingPercent);
    const pendingPct = Number.isFinite(rawPending)
      ? Math.max(0, Math.min(100, rawPending))
      : Math.max(0, 100 - completedPct);
    const completedCount = Number(chartData.completedCount) || 0;
    const pendingCount = Number(chartData.pendingCount) || 0;
    // Show 2 decimals when the value is tiny (< 1%) so a sliver like 0.02%
    // doesn't get rounded down to 0.0% in the centre label.
    const formatPct = (v: number) => {
      if (v === 0) return "0";
      if (v > 0 && v < 1) return v.toFixed(2);
      return v.toFixed(1);
    };
    const centerLabel = `${formatPct(completedPct)}%\nCompleted`;

    return {
      animationDuration: 1000,
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          if (params?.name === "Completed") {
            return `Completed: ${completedPct.toFixed(
              2
            )}%<br/>Closed tasks: ${completedCount}`;
          }
          if (params?.name === "Pending") {
            return `Pending: ${pendingPct.toFixed(
              2
            )}%<br/>Pending tasks: ${pendingCount}`;
          }
          return `${params?.name}: ${params?.value}`;
        },
      },
      legend: {
        orient: "horizontal",
        bottom: 0,
        data: ["Completed", "Pending"],
      },
      series: [
        {
          name: "Project Status",
          type: "pie",
          radius: ["55%", "75%"], // donut shape
          center: ["50%", "45%"],
          // Force a visible sliver for non-zero but tiny percentages
          // (e.g. closedePCR = 0.02% should still be a visible wedge,
          // not a hairline that disappears against the donut border).
          minAngle: 6,
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: "center",
            formatter: centerLabel,
            fontSize: 18,
            fontWeight: 600,
            color: "#0085e5",
            lineHeight: 24,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: "bold",
            },
          },
          labelLine: {
            show: false,
          },
          data: [
            {
              value: completedPct,
              name: "Completed",
              itemStyle: { color: "#0085e5" },
            },
            {
              value: pendingPct,
              name: "Pending",
              itemStyle: { color: "#ff841f" },
            },
          ],
        },
      ],
    };
  };

  if (loading) {
    return (
      <div
        style={{
          height: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  // Treat a zero-filled payload the same as "no data". ECharts renders an
  // all-zero pie as equal slices, which would wrongly show a 50/50 ring under
  // a "0% Completed" label, so bail out to the empty state instead.
  const hasNoData =
    !chartData ||
    ((Number(chartData.completedPercent) || 0) === 0 &&
      (Number(chartData.pendingPercent) || 0) === 0 &&
      (Number(chartData.completedCount) || 0) === 0 &&
      (Number(chartData.pendingCount) || 0) === 0);

  if (hasNoData) {
    return (
      <div
        style={{
          height: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Empty description="No data found" />
      </div>
    );
  }

  return (
    <ReactECharts
      option={getChartOptions()}
      style={{ height: 260, width: "100%" }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default ProjectCompletionDonutChart;
